import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Clock, Clock3, Flame, Sparkles, XCircle } from 'lucide-react';
import { ArcadeSummaryView } from '../components/ui';
import {
  abandonRapidFireRun,
  answerRapidFireQuestion,
  completeRapidFireRun,
  createRapidFireRun,
  getRapidFireRun,
  type ArcadeAnswerResult,
  type ArcadeCompleteResult,
  type ArcadeRun,
  type ArcadeRunState,
} from '../api/learning';
import './RapidFireGame.css';

type RapidFireBoard = ArcadeRun | ArcadeRunState;

type PendingAnswer = { questionId: string; answer: string };

function secondsUntil(deadlineAt: string | null): number | null {
  if (!deadlineAt) return null;
  return Math.max(0, Math.ceil((Date.parse(deadlineAt) - Date.now()) / 1000));
}

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function RapidFireGame() {
  const [relaxed, setRelaxed] = useState(false);
  const [run, setRun] = useState<RapidFireBoard | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [answeredIds, setAnsweredIds] = useState<string[]>([]);
  const [pending, setPending] = useState<PendingAnswer | null>(null);
  const [grade, setGrade] = useState<ArcadeAnswerResult | null>(null);
  const [summary, setSummary] = useState<ArcadeCompleteResult | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [streakDays, setStreakDays] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<'start' | 'grade' | 'recover' | null>(null);
  const finalizingRef = useRef(false);

  useEffect(() => {
    if (!run || run.timing === 'relaxed' || !run.deadlineAt || summary) {
      setRemainingSeconds(null);
      return;
    }
    const update = () => setRemainingSeconds(secondsUntil(run.deadlineAt));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [run, summary]);

  useEffect(() => {
    if (remainingSeconds !== 0 || !run || pending || finalizingRef.current) return;
    let cancelled = false;
    finalizingRef.current = true;
    setBusy(true);
    completeRapidFireRun(run.runId)
      .then(result => {
        if (!cancelled) setSummary(result);
      })
      .catch(() => {
        if (!cancelled) setError('recover');
      })
      .finally(() => {
        finalizingRef.current = false;
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pending, remainingSeconds, run]);

  const question = run?.questions.find(item => !answeredIds.includes(item.id)) ?? null;

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const created = await createRapidFireRun(relaxed ? 'relaxed' : 'standard');
      setRun(created);
      setRemainingSeconds(secondsUntil(created.deadlineAt));
      setAnswerText('');
      setAnsweredIds([]);
      setPending(null);
      setGrade(null);
      setSummary(null);
      setStreakDays(created.streakDays);
    } catch {
      setError('start');
    } finally {
      setBusy(false);
    }
  }

  function applyGrade(result: ArcadeAnswerResult) {
    setRun(current =>
      current
        ? {
            ...current,
            score: result.score,
            combo: result.combo,
            bestCombo: result.bestCombo,
            deadlineAt: result.deadlineAt,
            status: result.status,
            personalBest: result.personalBest,
            livesRemaining: result.livesRemaining,
          }
        : current
    );
    setAnsweredIds(current =>
      current.includes(result.questionId) ? current : [...current, result.questionId]
    );
    setAnswerText('');
    setPending(null);
    setGrade(result);
    setError(null);
    if (result.complete && result.verifiedScore !== null) {
      setSummary({
        runId: run?.runId ?? '',
        status: result.status,
        verifiedScore: result.verifiedScore,
        score: result.score,
        bestCombo: result.bestCombo,
        correctCount: result.correctCount,
        answeredCount: result.answeredCount,
        accuracy: result.accuracy,
        personalBest: result.personalBest ?? result.verifiedScore,
        isNewPersonalBest: result.isNewPersonalBest,
        streakDays,
        ghostOutcome: null,
      });
    }
  }

  async function submitAnswer(answer: string) {
    if (!run || !question || busy || remainingSeconds === 0) return;
    const next = { questionId: question.id, answer };
    setPending(next);
    setBusy(true);
    setError(null);
    try {
      applyGrade(await answerRapidFireQuestion(run.runId, question.id, answer));
    } catch {
      setError('grade');
    } finally {
      setBusy(false);
    }
  }

  async function retry() {
    if (!run || !pending) return;
    setBusy(true);
    setError(null);
    try {
      applyGrade(await answerRapidFireQuestion(run.runId, pending.questionId, pending.answer));
    } catch {
      setError('grade');
    } finally {
      setBusy(false);
    }
  }

  async function resume() {
    if (!run) return;
    setBusy(true);
    setError(null);
    try {
      const recovered = await getRapidFireRun(run.runId);
      setRun(recovered);
      setAnsweredIds(recovered.gradedQuestionIds);
      setAnswerText('');
      setPending(null);
      if (recovered.status !== 'in_progress' && recovered.verifiedScore !== null) {
        setSummary({
          runId: recovered.runId,
          status: recovered.status,
          verifiedScore: recovered.verifiedScore,
          score: recovered.score,
          bestCombo: recovered.bestCombo,
          correctCount: recovered.correctCount,
          answeredCount: recovered.answeredCount,
          accuracy: recovered.accuracy,
          personalBest: recovered.personalBest ?? recovered.verifiedScore,
          isNewPersonalBest: recovered.isNewPersonalBest,
          streakDays,
          ghostOutcome: null,
        });
      }
    } catch {
      setError('recover');
    } finally {
      setBusy(false);
    }
  }

  async function abandon() {
    if (!run) return;
    setBusy(true);
    try {
      await abandonRapidFireRun(run.runId);
      setRun(null);
      setAnswerText('');
      setAnsweredIds([]);
      setPending(null);
      setGrade(null);
      setError(null);
    } catch {
      setError('recover');
    } finally {
      setBusy(false);
    }
  }

  if (summary) {
    return (
      <ArcadeSummaryView
        heading="Time’s up!"
        verifiedScore={summary.verifiedScore}
        accuracy={summary.accuracy}
        correctCount={summary.correctCount}
        answeredCount={summary.answeredCount}
        isNewPersonalBest={summary.isNewPersonalBest}
        additionalStats={[{ label: 'Best combo', value: `×${summary.bestCombo}` }]}
        playAgainDisabled={busy}
        onPlayAgain={() => void start()}
      />
    );
  }

  if (!run) {
    return (
      <section aria-labelledby="rapid-heading">
        <div className="learning-section-heading">
          <div>
            <h2 id="rapid-heading">Rapid Fire</h2>
            <p>Answer as many questions as you can before the clock runs out.</p>
          </div>
        </div>
        {error === 'start' && <RapidError message="We could not start Rapid Fire." />}
        <div className="rapid-start-card">
          <Clock3 aria-hidden="true" />
          <p>
            {relaxed
              ? 'Take your time. The server still scores every answer.'
              : 'Race the round clock.'}
          </p>
          <label className="rapid-toggle">
            <input
              type="checkbox"
              checked={relaxed}
              onChange={event => setRelaxed(event.target.checked)}
            />
            Relaxed — no timer
          </label>
          <button className="rapid-primary" onClick={() => void start()} disabled={busy}>
            {busy ? 'Starting…' : 'Start Rapid Fire'}
          </button>
          {busy && (
            <span className="sr-only" role="status">
              Building Rapid Fire round…
            </span>
          )}
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="rapid-board-heading" aria-busy={busy}>
      <p className="sr-only" role="status">
        Rapid Fire round ready.
      </p>
      <div className="rapid-hud">
        <div>
          <span>Score</span>
          <strong>{run.score.toLocaleString()}</strong>
        </div>
        <div>
          <span>Combo</span>
          <strong>
            <Flame aria-hidden="true" /> ×{run.combo}
          </strong>
        </div>
        <div>
          <span>Time</span>
          <strong>
            {remainingSeconds === null ? (
              'No timer'
            ) : (
              <>
                <Clock aria-hidden="true" /> {formatTime(remainingSeconds)}
              </>
            )}
          </strong>
        </div>
      </div>

      <div className="learning-section-heading">
        <div>
          <h2 id="rapid-board-heading">Answer fast</h2>
          <p>{question?.prompt ?? ''}</p>
        </div>
        <span className="learning-count">
          {answeredIds.length} of {run.totalQuestions}
        </span>
      </div>

      {grade && (
        <div
          className={`rapid-feedback rapid-feedback--${grade.correct ? 'correct' : 'incorrect'}`}
          role="status"
          aria-live="polite"
        >
          {grade.correct ? <CheckCircle2 aria-hidden="true" /> : <XCircle aria-hidden="true" />}
          <span>
            {grade.correct
              ? `Correct · +${grade.scoreDelta} points · Combo ×${grade.combo}`
              : `Not quite. Correct answer: ${grade.correctAnswer}`}
          </span>
        </div>
      )}

      {error === 'grade' && pending && (
        <div className="rapid-recovery" role="alert">
          <p>Your answer was not confirmed. The round is paused at its last server state.</p>
          <button onClick={() => void retry()} disabled={busy}>
            Retry answer
          </button>
          <button onClick={() => void resume()} disabled={busy}>
            Resume from server
          </button>
          <button onClick={() => void abandon()} disabled={busy}>
            End unverified run
          </button>
        </div>
      )}
      {error === 'recover' && (
        <div className="rapid-recovery" role="alert">
          <p>The round could not be recovered.</p>
          <button onClick={() => void resume()} disabled={busy}>
            Try resume again
          </button>
          <button onClick={() => void abandon()} disabled={busy}>
            End unverified run
          </button>
        </div>
      )}

      {question?.type === 'word-match' && 'options' in question && (
        <div className="rapid-options" aria-label="Answer choices">
          {(question.options as string[]).map(option => (
            <button
              key={option}
              className="rapid-option"
              disabled={busy || remainingSeconds === 0}
              onClick={() => submitAnswer(option)}
            >
              {option}
            </button>
          ))}
        </div>
      )}

      {question?.type === 'fill-in-blank' && 'sentence' in question && (
        <div className="rapid-blank-card">
          <p className="rapid-sentence">{question.sentence as string}</p>
          <div className="rapid-input-row">
            <input
              className="rapid-text-input"
              type="text"
              aria-label="Your answer"
              value={answerText}
              onChange={e => setAnswerText(e.target.value)}
              placeholder="Type your answer…"
              disabled={busy || remainingSeconds === 0}
              autoComplete="off"
            />
            <button
              className="rapid-primary"
              disabled={busy || !answerText.trim() || remainingSeconds === 0}
              onClick={() => submitAnswer(answerText.trim())}
            >
              {busy ? 'Grading…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {!question && !summary && (
        <div className="rapid-empty">
          <Sparkles aria-hidden="true" />
          <h3>All questions answered</h3>
          <button
            className="rapid-primary"
            onClick={() => void completeRapidFireRun(run.runId).then(setSummary)}
            disabled={busy}
          >
            See results
          </button>
        </div>
      )}

      <button className="rapid-abandon" onClick={() => void abandon()} disabled={busy}>
        End run without saving a score
      </button>
    </section>
  );
}

function RapidError({ message }: { message: string }) {
  return (
    <div className="learning-notice learning-notice--error" role="alert">
      <XCircle aria-hidden="true" /> <span>{message}</span>
    </div>
  );
}
